"use client";
import { BlogRelatedBrand } from "@v2/types/blog";
import { Box, Button, colors, Typography } from "@mui/material";
import MediaV2 from "@v2/components/MediaV2";
import Link from "next/link";
import { ChevronLeft } from "lucide-react";

interface Props {
    brands: BlogRelatedBrand[];
    noMargin?: boolean;
}

export default function BlogRelatedBrands({ brands, noMargin }: Props) {
    if (!brands.length) return null;

    if (brands.length === 1) {
        return <SingleBrandBox brand={brands[0]} noMargin={noMargin} />;
    }

    return <MultiBrandList brands={brands} />;
}

function SingleBrandBox({ brand, noMargin }: { brand: BlogRelatedBrand; noMargin?: boolean }) {
    const hasBanner = !!brand.banner?.image;

    if (hasBanner) {
        return (
            <Box sx={{ display: "flex", gap: 1, mb: noMargin ? 0 : 2 }}>
                <BrandInfoCard brand={brand} sx={{ width: "50%" }} />

                {/* banner image — left side in RTL */}
                <Box
                    component={Link}
                    href={brand.banner!.link ?? `/fa/brand/${brand.slug}`}
                    prefetch={false}
                    aria-label={brand.banner!.title || `مشاهده ${brand.fa_name || brand.en_name || 'برند'}`}
                    sx={{
                        width: "50%",
                        aspectRatio: "3/4",
                        position: "relative",
                        flexShrink: 0,
                        borderRadius: 1.5,
                        overflow: "hidden",
                        display: "block",
                        transition: "all 180ms ease",
                        "&:hover": { boxShadow: "0 4px 14px rgba(0,0,0,0.15)", transform: "translateY(-2px)" },
                    }}
                >
                    <MediaV2
                        media_id={brand.banner!.image!.id.toString()}
                        alt={brand.banner!.title || brand.en_name || brand.fa_name || ""}
                        options={{ action: "cropThumbnail", quality: 85 }}
                        imageProps={{ fill: true, style: { objectFit: "cover" } }}
                        boxProps={{ sx: { width: 1, height: 1 } }}
                    />
                    {(brand.banner!.title || brand.banner!.en_title) && (
                        <Box sx={{
                            position: "absolute",
                            bottom: 0,
                            insetInline: 0,
                            background: "linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.55) 25%, rgba(0,0,0,0.28) 55%, rgba(0,0,0,0.06) 80%, transparent 100%)",
                            px: 1.5,
                            pt: 5,
                            pb: 1.5,
                        }}>
                            {brand.banner!.title && (
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#fff", lineHeight: 1.4 }}>
                                    {brand.banner!.title}
                                </Typography>
                            )}
                            {brand.banner!.en_title && (
                                <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.7)", mt: 0.25 }}>
                                    {brand.banner!.en_title}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        );
    }

    // vertical layout when placed next to collection (noMargin)
    if (noMargin) {
        return <BrandInfoCard brand={brand} sx={{ height: "100%" }} />;
    }

    // horizontal layout standalone
    return (
        <Box
            component={Link}
            href={`/fa/brand/${brand.slug}`}
            prefetch={false}
            aria-label={`مشاهده محصولات ${brand.fa_name || brand.en_name || ''}`}
            sx={{
                display: "flex",
                textDecoration: "none",
                borderRadius: 1.5,
                overflow: "hidden",
                bgcolor: "#fff",
                mb: 2,
                border: `1px solid ${colors.grey[100]}`,
                transition: "box-shadow 180ms ease, transform 180ms ease",
                "&:hover": {
                    boxShadow: "rgba(0,0,0,0.12) 0px 8px 24px",
                    transform: "translateY(-2px)",
                },
            }}
        >
            {/* logo — right side in RTL */}
            <Box sx={{ width: "50%", display: "flex", alignItems: "center", justifyContent: "center", py: 2.5, px: 1 }}>
                {brand.logo_id ? (
                    <Box sx={{ position: "relative", width: 160, height: 76 }}>
                        <MediaV2
                            media_id={brand.logo_id}
                            alt={brand.en_name || brand.fa_name || ""}
                            options={{ action: "thumbnail", quality: 90 }}
                            imageProps={{ fill: true, style: { objectFit: "contain" }, loading: "lazy" }}
                            boxProps={{ sx: { width: 1, height: 1 } }}
                        />
                    </Box>
                ) : null}
            </Box>

            {/* content — left side in RTL */}
            <Box sx={{
                width: "50%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                py: 2.5,
                px: 1.5,
            }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                    {brand.fa_name && (
                        <Typography sx={{ fontSize: 20, fontWeight: 700, color: colors.grey[900], textAlign: "center", lineHeight: 1.4 }}>
                            {brand.fa_name}
                        </Typography>
                    )}
                    {brand.en_name && (
                        <Typography sx={{ fontSize: 12, color: colors.grey[700], textAlign: "center" }}>
                            {brand.en_name}
                        </Typography>
                    )}
                </Box>
                <Button
                    component="span"
                    variant="contained"
                    disableElevation
                    endIcon={<ChevronLeft size={13} />}
                    sx={{
                        fontSize: 12,
                        height: 34,
                        px: 2,
                        bgcolor: colors.grey[900],
                        color: "#fff",
                        "&:hover": { bgcolor: colors.grey[800] },
                    }}
                >
                    مشاهده محصولات
                </Button>
            </Box>
        </Box>
    );
}

function BrandInfoCard({ brand, sx }: { brand: BlogRelatedBrand; sx?: object }) {
    return (
        <Box
            component={Link}
            href={`/fa/brand/${brand.slug}`}
            prefetch={false}
            aria-label={`مشاهده محصولات ${brand.fa_name || brand.en_name || ''}`}
            sx={{
                display: "flex",
                flexDirection: "column",
                textDecoration: "none",
                color: "inherit",
                borderRadius: 1.5,
                overflow: "hidden",
                bgcolor: "#fff",
                border: `1px solid ${colors.grey[100]}`,
                transition: "box-shadow 180ms ease, transform 180ms ease",
                "&:hover": { boxShadow: "0 4px 14px rgba(0,0,0,0.08)", transform: "translateY(-2px)" },
                ...sx,
            }}
        >
            {/* logo + name — centered */}
            <Box sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                px: 1.5,
                pt: 1.5,
            }}>
                {brand.logo_id && (
                    <Box sx={{ width: 1, height: 72, position: "relative" }}>
                        <MediaV2
                            media_id={brand.logo_id}
                            alt={brand.en_name || brand.fa_name || ""}
                            options={{ action: "thumbnail", quality: 90 }}
                            imageProps={{ fill: true, style: { objectFit: "contain" }, loading: "lazy" }}
                            boxProps={{ sx: { width: 1, height: 1 } }}
                        />
                    </Box>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
                    {brand.fa_name && (
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.grey[900], textAlign: "center", lineHeight: 1.4 }}>
                            {brand.fa_name}
                        </Typography>
                    )}
                    {brand.en_name && (
                        <Typography sx={{ fontSize: 11, color: colors.grey[700], textAlign: "center" }}>
                            {brand.en_name}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* button — bottom */}
            <Box sx={{ display: "flex", justifyContent: "center", pb: 1.5, px: 1.5 }}>
                <Button
                    component="span"
                    variant="contained"
                    disableElevation
                    endIcon={<ChevronLeft size={13} />}
                    sx={{
                        fontSize: 12,
                        height: 34,
                        px: 2,
                        bgcolor: colors.grey[900],
                        color: "#fff",
                        "&:hover": { bgcolor: colors.grey[800] },
                    }}
                >
                    مشاهده محصولات
                </Button>
            </Box>
        </Box>
    );
}

function MultiBrandList({ brands }: { brands: BlogRelatedBrand[] }) {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {brands.map((brand) => (
                <Box
                    key={brand.id}
                    component={Link}
                    href={`/fa/brand/${brand.slug}`}
                    prefetch={false}
                    aria-label={`مشاهده محصولات ${brand.fa_name || brand.en_name || ''}`}
                    sx={{
                        display: "flex",
                        textDecoration: "none",
                        borderRadius: 1.5,
                        overflow: "hidden",
                        bgcolor: "#fff",
                        border: `1px solid ${colors.grey[100]}`,
                        transition: "box-shadow 180ms ease, transform 180ms ease",
                        "&:hover": {
                            boxShadow: "rgba(0,0,0,0.12) 0px 8px 24px",
                            transform: "translateY(-2px)",
                        },
                    }}
                >
                    {/* logo — right side in RTL */}
                    <Box sx={{ width: "50%", display: "flex", alignItems: "center", justifyContent: "center", py: 2, px: 1 }}>
                        {brand.logo_id ? (
                            <Box sx={{ position: "relative", width: 140, height: 64 }}>
                                <MediaV2
                                    media_id={brand.logo_id}
                                    alt={brand.en_name || brand.fa_name || ""}
                                    options={{ action: "thumbnail", quality: 90 }}
                                    imageProps={{ fill: true, style: { objectFit: "contain" }, loading: "lazy" }}
                                    boxProps={{ sx: { width: 1, height: 1 } }}
                                />
                            </Box>
                        ) : null}
                    </Box>

                    {/* content — left side in RTL */}
                    <Box sx={{
                        width: "50%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 2,
                        px: 1.5,
                        gap: 1,
                    }}>
                        {brand.fa_name && (
                            <Typography sx={{ fontSize: 18, fontWeight: 700, color: colors.grey[900], textAlign: "center", lineHeight: 1.4 }}>
                                {brand.fa_name}
                            </Typography>
                        )}
                        {brand.en_name && (
                            <Typography sx={{ fontSize: 11, color: colors.grey[500], textAlign: "center" }}>
                                {brand.en_name}
                            </Typography>
                        )}
                        <Button
                            component="span"
                            variant="contained"
                            disableElevation
                            endIcon={<ChevronLeft size={13} />}
                            sx={{
                                fontSize: 12,
                                height: 34,
                                px: 2,
                                bgcolor: colors.grey[900],
                                color: "#fff",
                                "&:hover": { bgcolor: colors.grey[800] },
                            }}
                        >
                            مشاهده محصولات
                        </Button>
                    </Box>
                </Box>
            ))}
        </Box>
    );
}
